import { UserResConstantsCodeType } from 'src/types/user';
import translate from '@translate';
import onlyUnique from 'src/utils/onlyUnique';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import {
    applicationMessageTypesColors, applicationMessageTypesOrder,
    batteryTypeColors,
    claimStatusColors,
    claimStatusOrder,
    orderStatusColors,
    providerStatusColors,
    providerStatusOrder,
    receivingStatusColors,
    receivingStatusOrder,
    returnStatusColors,
    returnStatusOrder,
    transportStatusColors
} from './constants';

type GenericObject = {
    [key: string]: any;
};
const sortByOrder = <T extends GenericObject>(arr: T[], order: string[], key: keyof T): T[] => {
    return [...arr].sort((a, b) => {
        const indexA = order.indexOf(a[key]);
        const indexB = order.indexOf(b[key]);

        if (indexA === -1) return 1; // If not found, move to the end
        if (indexB === -1) return -1;

        return indexA - indexB; // Sort based on the order index
    });
};

export const sortByClaimStatusOrder = (
    codes: Array<ConstantsCodeType | undefined>
): ConstantsCodeType[] => {
    const defined = codes.filter((code): code is ConstantsCodeType => code !== undefined);
    return sortByOrder(defined, claimStatusOrder, 'value');
};

export const translateOptions = (options: ConstantsCodeType[]): ConstantsCodeType[] => {
    return options.map((option) => ({ ...option, label: option.label ? translate(option.label) : '' }));
};

export const formatSimpleCodes = (
    data: UserResConstantsCodeType[] | undefined | null,
    constantKey: string,
    translate?: boolean,
    numberValue?: boolean
): ConstantsCodeType[] => {
    const safeData = data ?? [];
    if (!translate) {
        return safeData.map(
            (item: UserResConstantsCodeType): ConstantsCodeType => ({
                label: item.LABEL,
                rawLabel: item.LABEL,
                value: numberValue ? Number(item.VALUE) : item.VALUE,
            })
        );
    }
    return translateOptions(
        safeData.map(
            (item: UserResConstantsCodeType): ConstantsCodeType => ({
                label: `utils.${constantKey}.${item.LABEL}`,
                rawLabel: item.LABEL,
                value: numberValue ? Number(item.VALUE) : item.VALUE,
            })
        )
    );
};

export const formatDriverCompanyCodes = (
    data: UserResConstantsCodeType[] | undefined | null,
    constantKey: string
): ConstantsCodeType[] => {
    const options = (data ?? []).map(
        (item: UserResConstantsCodeType): ConstantsCodeType => ({
            label: item.LABEL,
            rawLabel: item.LABEL,
            value: item.VALUE,
        })
    );

    // Sort alphabetically with 'other' always at the end
    return options.sort((a, b) => {
        if (a.value === 'other') return 1;
        if (b.value === 'other') return -1;
        return a.label.localeCompare(b.label);
    });
};

export const formatColorStatusCodes = (
    data: UserResConstantsCodeType[] | undefined | null,
    constantKey: string,
    statusColors?: { [key: string]: string },
    order?: string[]
): ConstantsCodeType[] => {
    const safeData = data ?? [];
    return translateOptions(
        (order ? sortByOrder(safeData, order, 'VALUE') : safeData).map(
            (item: UserResConstantsCodeType): ConstantsCodeType => ({
                label: `utils.${constantKey}.${item.LABEL}`,
                rawLabel: item.LABEL,
                value: item.VALUE,
                color: statusColors ? statusColors[item.VALUE] : undefined,
            })
        )
    );
};

export const formatOrderStatusCodes = (
    data: UserResConstantsCodeType[] | undefined | null,
    statusColors: { [key: string]: string }
): ConstantsCodeType[] => {
    return translateOptions(
        (data ?? [])
            .sort(
                (a: UserResConstantsCodeType, b: UserResConstantsCodeType): any =>
                    (a.ORDER_INT || 0) - (b.ORDER_INT || 0)
            )
            .map(
                (item: UserResConstantsCodeType): ConstantsCodeType => ({
                    label: `utils.orderStatusCodes.${item.LABEL}`,
                    rawLabel: item.LABEL,
                    value: item.VALUE,
                    editOrder: !!item.EDIT_ORDER,
                    ORDER_INT: item.ORDER_INT,
                    color: statusColors[item.VALUE],
                })
            )
    );
};

export const formatReceivingStatusCodes = (
    data: UserResConstantsCodeType[] | undefined | null,
    statusColors: { [key: string]: string },
    order: string[]
): ConstantsCodeType[] => {
    return translateOptions(
        sortByOrder(data ?? [], order, 'VALUE').map((item: any): any => ({
            label: `utils.receivingStatusCode.${item.LABEL}`,
            rawLabel: item.LABEL,
            value: item.VALUE,
            awaiting: !!item.AWAITING,
            color: statusColors[item.VALUE],
        }))
    );
};

export const formatShipperMessageCodes = (data: UserResConstantsCodeType[] | undefined | null): ConstantsCodeType[] => {
    return translateOptions(
        (data ?? []).map((item: any): any => ({
            label: item.LABEL,
            rawLabel: item.LABEL,
            value: item.VALUE,
            shipperCode: item.SHIPPER_CODE,
            LANG: item.LANG,
        }))
    );
};

export const formatServiceCodes = (data: UserResConstantsCodeType[] | undefined | null): ConstantsCodeType[] => {
    return (data ?? []).map(
        (item: UserResConstantsCodeType): ConstantsCodeType => ({
            id: item.ID,
            label: item.LABEL,
            rawLabel: item.LABEL,
            value: item.VALUE,
            shipperCode: item.SHIPPER_CODE,
            isReturn: item.IS_RETURN,
            isExpedition: item.IS_EXPEDITION,
            isParcel: item.IS_PARCEL,
            isPallet: item.IS_PALLET,
            COUNTRY_SERVICE_CODES: item.COUNTRY_SERVICE_CODES?.map((item) => item.COUNTRY_CODE).filter(onlyUnique),
        })
    );
};

export const formatTemplateDataStructureCodes = (data: UserResConstantsCodeType[] | undefined | null): ConstantsCodeType[] => {
    return (data ?? []).map((item: any): any => ({
        label: item.LABEL,
        rawLabel: item.LABEL,
        value: item.VALUE,
        json: item.JSON,
        description: item.DESCRIPTION,
    }));
};

export const formatCodes = (data: UserResConstantsCodeType[] | undefined | null, constantKey: string) => {
    const safeData = data ?? [];
    switch (constantKey) {
        case 'orderStatusCodes':
            return formatOrderStatusCodes(safeData, orderStatusColors);
        case 'receivingStatusCodes':
            return formatReceivingStatusCodes(safeData, receivingStatusColors, receivingStatusOrder);
        case 'serviceCodes':
            return formatServiceCodes(safeData);
        case 'claimStatusCodes':
            return formatColorStatusCodes(safeData, constantKey, claimStatusColors, claimStatusOrder);
        case 'transportStatusCodes':
            return formatColorStatusCodes(safeData, constantKey, transportStatusColors);
        case 'returnStatusCodes':
            return formatColorStatusCodes(safeData, constantKey, returnStatusColors, returnStatusOrder);
        case 'applicationMessageCodes':
            return formatColorStatusCodes(safeData, constantKey, applicationMessageTypesColors, applicationMessageTypesOrder);
        case 'shipperMessageCodes':
            return formatShipperMessageCodes(safeData);
        case 'driverCompanyCodes':
            return formatDriverCompanyCodes(safeData, constantKey);
        case 'shipperCodes':
        case 'emailTemplateTypeCodes':
        case 'providerSystemCodes':
            return formatSimpleCodes(safeData, constantKey);
        case 'insertByOrderTypeCodes':
            // Use translations under utils.insertByOrderTypeCodes.*
            return formatSimpleCodes(safeData, constantKey, true);
        case 'orderPriorityCodes':
            return formatSimpleCodes(safeData, constantKey, true, true);
        case 'templateDataStructureCodes':
            return formatTemplateDataStructureCodes(safeData);
        case 'claimsResolverCodes':
            return formatColorStatusCodes(safeData, constantKey, undefined, ['carrier', 'skladonIT', 'skladon'])
        case 'orderMarketplacesCodes':
            return formatColorStatusCodes(safeData, constantKey, undefined, ['allegro', 'alza', 'kaufland', 'mall', 'other'])
        case 'providerStatusCodes':
            return formatColorStatusCodes(safeData, constantKey, providerStatusColors, providerStatusOrder);
        case 'batteryTypeCodes':
            return formatColorStatusCodes(safeData, constantKey, batteryTypeColors);
        case 'stockMovementTypeCodes':
            return formatSimpleCodes(safeData, constantKey, true);
        case 'salesRepresentativeCodes':
            return formatSimpleCodes(safeData, constantKey, false);
        case 'keyAccountManagerCodes':
            return formatSimpleCodes(safeData, constantKey, false);
        case 'receivingPriorityCodes':
            return formatSimpleCodes(safeData, constantKey, true, true);
        default:
            return formatSimpleCodes(safeData, constantKey, true);
    }
};

export const getCodesObject = (data: ConstantsCodeType[]): ConstantsCodesObjectType => {
    const obj: ConstantsCodesObjectType = {};
    data.forEach((item) => {
        obj[String(item.value)] = item;
    });

    return obj;
};

export const formatFromCodes = (data: ConstantsCodeType[], type: string): ConstantsCodeType[] => {
    switch (type) {
        case 'shipperMessageCodes':
            return data.map((item: any): any => ({
                LABEL: item.label || item.rawLabel,
                VALUE: item.value,
                SHIPPER_CODE: item.shipperCode,
                LANG: item.LANG,
            }));
        case 'serviceCodes':
            return data.map((item: any): any => ({
                LABEL: item.label || item.rawLabel,
                VALUE: item.value,
                SHIPPER_CODE: item.shipperCode,
                IS_EXPEDITION: item.isExpedition,
                IS_RETURN: item.isReturn,
                IS_PARCEL: item.isParcel,
                IS_PALLET: item.isPallet,
                COUNTRY_SERVICE_CODES: item.COUNTRY_SERVICE_CODES.map((itemCountry: any): any => ({
                    COUNTRY_CODE: itemCountry,
                })),
            }));

        default:
            return data.map((item: any): any => ({
                LABEL: item.label || item.rawLabel,
                VALUE: item.value,
            }));
    }
};
